import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Calendar, MapPin, Users, Trash2, Image as ImageIcon, Plus, X, MessageCircle } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import AuthImage from "@/components/AuthImage";
import Avatar from "@/components/Avatar";

function formatWhen(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  return d.toLocaleString(undefined, {
    weekday: "long",
    month: "long",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

export default function EventDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const [event, setEvent] = useState(null);
  const [attendees, setAttendees] = useState([]);
  const [err, setErr] = useState("");
  const [working, setWorking] = useState(false);
  const photoRef = useRef(null);
  const [photoUploading, setPhotoUploading] = useState(false);

  const load = useCallback(async () => {
    try {
      const [r1, r2] = await Promise.all([
        api.get(`/events/${id}`),
        api.get(`/events/${id}/attendees`),
      ]);
      setEvent(r1.data);
      setAttendees(r2.data);
    } catch (e) {
      setErr(formatApiError(e));
    }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  const toggleRsvp = async () => {
    if (!event) return;
    setWorking(true);
    setErr("");
    try {
      await api.post(`/events/${id}/rsvp`);
      load();
    } catch (e) {
      setErr(formatApiError(e));
    } finally {
      setWorking(false);
    }
  };

  const remove = async () => {
    if (!window.confirm("Delete this event?")) return;
    try {
      await api.delete(`/events/${id}`);
      nav("/feed", { replace: true });
    } catch (e) {
      setErr(formatApiError(e));
    }
  };

  const onAddPhoto = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    if (!f.type.startsWith("image/")) { setErr("Only image files are allowed."); return; }
    if (f.size > 8 * 1024 * 1024) { setErr("Image too large (max 8MB)."); return; }
    setErr(""); setPhotoUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const up = await api.post("/upload", fd);
      const r = await api.post(`/events/${id}/photos`, { image_path: up.data.path });
      setEvent(r.data);
    } catch (e2) {
      setErr(formatApiError(e2));
    } finally {
      setPhotoUploading(false);
    }
  };

  const removePhoto = async (p) => {
    if (!window.confirm("Remove this photo?")) return;
    try {
      const r = await api.delete(`/events/${id}/photos`, { data: { image_path: p } });
      setEvent(r.data);
    } catch (e) {
      setErr(formatApiError(e));
    }
  };

  if (!event) {
    return (
      <div className="py-12 text-center text-white/40 text-sm tracking-widest uppercase">Loading…</div>
    );
  }

  const isMine = event.creator?.id === user?.id || user?.role === "admin";
  const full = event.attendee_count >= event.capacity;
  const past = new Date(event.starts_at).getTime() < Date.now();

  return (
    <div className="space-y-6 max-w-3xl mx-auto fade-up">
      <button
        onClick={() => nav(-1)}
        className="text-white/60 hover:text-white text-xs uppercase tracking-widest flex items-center gap-1"
        data-testid="event-back"
      >
        <ArrowLeft size={14} strokeWidth={1.5} /> Back
      </button>

      {/* Cover */}
      <div className="border border-white/10 rounded-md overflow-hidden bg-[#141414]">
        <div className="h-56 sm:h-72 bg-black">
          {event.image_path ? (
            <AuthImage path={event.image_path} className="w-full h-full object-cover" />
          ) : (
            <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-[#1a1a1a] to-[#0a0a0a]">
              <Calendar size={36} strokeWidth={1} className="text-[#d4af37]/40" />
            </div>
          )}
        </div>
        <div className="p-5 space-y-3">
          <p className="text-[#d4af37] text-xs uppercase tracking-[0.3em]">
            {event.creator?.role === "admin" ? "Concierge event" : "Member event"} · by {event.creator?.name}
          </p>
          <h1 className="font-serif text-3xl sm:text-4xl">{event.title}</h1>
          <div className="text-sm text-white/70 flex items-center gap-2"><Calendar size={14} strokeWidth={1.5} className="text-white/40" /> {formatWhen(event.starts_at)}</div>
          <div className="text-sm text-white/70 flex items-center gap-2"><MapPin size={14} strokeWidth={1.5} className="text-white/40" /> {event.location}</div>
          <div className="text-sm text-white/70 flex items-center gap-2"><Users size={14} strokeWidth={1.5} className="text-white/40" /> {event.attendee_count} / {event.capacity} attending</div>
          {event.description && (
            <p className="text-sm text-white/80 leading-relaxed whitespace-pre-wrap pt-2">{event.description}</p>
          )}
        </div>
      </div>

      {err && <p className="text-sm text-red-400" data-testid="event-error">{err}</p>}

      {/* Actions */}
      <div className="flex items-center gap-3 flex-wrap">
        <button
          onClick={toggleRsvp}
          disabled={working || past || (full && !event.going)}
          className={`px-6 py-3 text-xs uppercase tracking-[0.2em] disabled:opacity-50 ${
            event.going
              ? "border border-[#d4af37]/60 text-[#d4af37] hover:bg-[#d4af37]/10"
              : "bg-[#d4af37] text-black hover:bg-[#f3e5ab]"
          }`}
          data-testid="event-rsvp"
        >
          {past ? "Event ended" : event.going ? "✓ Going — cancel" : full ? "Event full" : "RSVP · 参与"}
        </button>
        {isMine && (
          <>
            <input ref={photoRef} type="file" accept="image/*" className="hidden" onChange={onAddPhoto} data-testid="event-photo-input" />
            <button
              onClick={() => photoRef.current?.click()}
              disabled={photoUploading}
              className="border border-white/15 hover:border-[#d4af37]/60 text-white/70 hover:text-white px-4 py-3 text-xs uppercase tracking-widest disabled:opacity-50 flex items-center gap-2"
              data-testid="event-add-photo"
            >
              <Plus size={14} strokeWidth={1.5} /> {photoUploading ? "Uploading…" : "Add photo"}
            </button>
            <button
              onClick={remove}
              className="border border-white/15 hover:border-red-400/40 hover:text-red-300 text-white/70 px-4 py-3 text-xs uppercase tracking-widest flex items-center gap-2"
              data-testid="event-delete"
            >
              <Trash2 size={14} strokeWidth={1.5} /> Delete
            </button>
          </>
        )}
      </div>

      {/* Extra photos gallery */}
      {(event.photos || []).length > 0 && (
        <div className="space-y-3">
          <p className="text-[10px] uppercase tracking-[0.25em] text-[#d4af37]">Gallery</p>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2" data-testid="event-photos-grid">
            {event.photos.map((p) => (
              <div key={p} className="relative bg-black aspect-square border border-white/10 rounded overflow-hidden">
                <AuthImage path={p} className="w-full h-full object-cover" />
                {isMine && (
                  <button
                    onClick={() => removePhoto(p)}
                    className="absolute top-1 right-1 bg-black/70 rounded-full p-1 text-white/80 hover:text-red-300 border border-white/15"
                    data-testid="event-photo-remove"
                    aria-label="Remove photo"
                  >
                    <X size={12} strokeWidth={2} />
                  </button>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Attendees */}
      <div className="space-y-3">
        <p className="text-[10px] uppercase tracking-[0.25em] text-[#d4af37]">Attendees · {attendees.length}</p>
        {attendees.length === 0 ? (
          <p className="text-sm text-white/40">No-one yet. Be the first to RSVP.</p>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2" data-testid="event-attendees">
            {attendees.map((a) => (
              <div key={a.id} className="flex items-center gap-3 bg-[#141414] border border-white/10 rounded p-2">
                <Avatar name={a.name} path={a.avatar_path} size={36} />
                <div className="min-w-0">
                  <div className="text-sm truncate">{a.name}</div>
                  <div className="text-[10px] uppercase tracking-widest text-white/40 truncate">{a.occupation || "—"}</div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
